import { matchSorter } from "match-sorter";
import { useCallback, useState } from "react";
import { useQueryParam } from "shared-ui";

import { useKycValidation, usePersonValidation, type Configuration as KycConfiguration } from "..";
import type { PersonIdvState, PersonSummary } from "../components";
import { KycFormProvider, useKycForms } from "../contexts";
import { signerValidationByCountry, type SignerValidationByCountry } from "../utils";

import { useUploadManager } from "./useUploadManager";

const selectedCompany = {
  name: "Wayflyer Limited",
  company_number: "US602786",
  country: "US",
  state: "NY",
  identity_number: "01-1234567",
  is_operating_address_same_as_registered_address: false,
  registered_address_in_full: "82 Nassau St New York, NY 1003",
  no_UBOs_attested: true,
};

export const associatedPersons: PersonSummary[] = [
  {
    serverId: 1,
    name: "Nathan Power",
    role: "No significant role",
    isSignee: false,
    requiresIdv: false,
    isMissingInformation: false,
    isEligibleSignee: false,
    isMemberOfCompany: true,
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
  {
    serverId: 2,
    name: "Mike Miller",
    role: "President, UBO",
    isUbo: true,
    isSignee: true,
    requiresIdv: true,
    isMissingInformation: false,
    isEligibleSignee: true,
    isMemberOfCompany: true,
    idvSessionStatus: { status: "requires_input" },
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
  {
    serverId: 3,
    name: "Darren Burke",
    role: "Chief Executive Officer, UBO",
    isUbo: true,
    isSignee: false,
    requiresIdv: true,
    isMissingInformation: false,
    isEligibleSignee: true,
    isMemberOfCompany: false,
    idvSessionStatus: { status: "requires_input", last_error: { code: "consent_declined", reason: "" } },
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
  {
    serverId: 4,
    name: "Serafima Sergeeva",
    role: "Chief Executive Officer, UBO",
    isUbo: true,
    isSignee: false,
    requiresIdv: true,
    isMissingInformation: false,
    isEligibleSignee: true,
    isMemberOfCompany: true,
    idvSessionStatus: { status: "processing" },
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
  {
    serverId: 5,
    name: "Ed Doyle",
    role: "UBO",
    isSignee: false,
    requiresIdv: true,
    isMissingInformation: false,
    isEligibleSignee: true,
    isMemberOfCompany: true,
    idvSessionStatus: { status: "verified" },
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
];

export const associatedPersonsNoMatchingEmailsForAuthorizedOfficers: PersonSummary[] = [
  {
    serverId: 1,
    name: "Nathan Power",
    role: "No significant role",
    isSignee: false,
    requiresIdv: false,
    isMissingInformation: false,
    isEligibleSignee: true,
    isMemberOfCompany: false,
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
  {
    serverId: 2,
    name: "Mike Miller",
    role: "President, UBO",
    isUbo: true,
    isSignee: true,
    requiresIdv: true,
    isMissingInformation: false,
    isEligibleSignee: true,
    isMemberOfCompany: false,
    idvSessionStatus: { status: "requires_input" },
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
  {
    serverId: 3,
    name: "Darren Burke",
    role: "Chief Executive Officer, UBO",
    isUbo: true,
    isSignee: false,
    requiresIdv: true,
    isMissingInformation: false,
    isEligibleSignee: false,
    isMemberOfCompany: false,
    idvSessionStatus: { status: "requires_input", last_error: { code: "consent_declined", reason: "" } },
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
];

export const associatedPersonsNoAuthorizedOfficers: PersonSummary[] = [
  {
    serverId: 1,
    name: "Nathan Power",
    role: "No significant role",
    isSignee: false,
    requiresIdv: false,
    isMissingInformation: false,
    isEligibleSignee: false,
    isMemberOfCompany: true,
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
  {
    serverId: 2,
    name: "Mike Miller",
    role: "President, UBO",
    isUbo: true,
    isSignee: true,
    requiresIdv: true,
    isMissingInformation: false,
    isEligibleSignee: false,
    isMemberOfCompany: true,
    idvSessionStatus: { status: "requires_input" },
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
  {
    serverId: 3,
    name: "Darren Burke",
    role: "Chief Executive Officer, UBO",
    isUbo: true,
    isSignee: false,
    requiresIdv: true,
    isMissingInformation: false,
    isEligibleSignee: false,
    isMemberOfCompany: false,
    idvSessionStatus: { status: "requires_input", last_error: { code: "consent_declined", reason: "" } },
    address: "some address",
    dob: "11/11/2000",
    countryOfBirth: "Ukrain",
    cityOfBirth: "Kyiv",
    ssn: "123123",
    email: "test@testmail.com",
  },
];

const defaultValues = {
  "company-info-reg-ein": selectedCompany.identity_number,
  "company-info-reg-country": selectedCompany.country,
  "company-info-reg-state": selectedCompany.state,
  "company-info-company-number": selectedCompany.company_number,
  "company-info-company-name": selectedCompany.name,
  "company-info-company-full-address": selectedCompany.registered_address_in_full,
  "company-info-reg-same-address": true,
  "company-info-op-address-1": "",
  "company-info-op-address-2": "",
  "company-info-op-state": "",
  "company-info-op-city": "",
  "company-info-op-zip": "",
  "company-info-registered-address-1": "",
  "company-info-registered-address-2": "",
  "company-info-registered-state": "",
  "company-info-registered-city": "",
  "company-info-registered-zip": "",
  "company-info-date-of-incorporation": "2020-01-01",
  "company-info-website": "www.wayflyer.com",
  "company-info-primary-account-holder": null,
  "associated-persons-no-ubos": false,
  "associated-persons": associatedPersons,
};

const options = [
  {
    name: "Wayflyer Limited",
    company_number: "US602786",
    registered_address_in_full: "82 Nassau St New York, NY 1003",
  },
  {
    name: "Biggly Limited",
    company_number: "EEOO123",
    registered_address_in_full: null,
  },
];

function filterItems(options, searchTerm) {
  return searchTerm
    ? matchSorter(options, searchTerm, {
        keys: ["name", "company_number"],
        threshold: matchSorter.rankings.CONTAINS,
      })
    : options;
}

const FormWrapper = ({ action, children, kycConfiguration }) => {
  const { setQueryParam } = useQueryParam("step");
  const { personForm } = useKycForms();
  const { kycForm } = useKycForms();
  const [personIdvState, setPersonIdvState] = useState<PersonIdvState>();
  const [isManualCompanyEntry, setIsManualCompanyEntry] = useState<boolean>(false);

  const [filteredOptions, setFilteredOptions] = useState(undefined);
  const [fetching, setFetching] = useState(false);
  const onCompanySearchTermChange = useCallback((searchTerm) => {
    setFetching(true);
    setTimeout(() => {
      setFilteredOptions(!searchTerm ? [] : filterItems(options, searchTerm));
      setFetching(false);
    }, 800);
  }, []);

  const onSubmitClickHandler = useCallback(() => {
    action?.("onSubmitClickHandler");
  }, [action]);

  const onConfirmClickHandler = useCallback(() => {
    action?.("onConfirmClickHandler");
  }, [action]);

  const onConfirmCancelClickHandler = useCallback(() => {
    action?.("onConfirmCancelClickHandler");
  }, [action]);

  const onValidateClickHandler = useCallback(() => {
    action?.("onValidateClickHandler");
  }, [action]);

  const onSignificantInteractionHandler = useCallback(() => {
    action?.("onNextClickHandler");
  }, [action]);

  const onUpdatePersonClickHandler = useCallback(() => {
    action?.("onUpdatePersonClickHandler");
  }, [action]);

  const onRemovePerson = useCallback(() => {
    action?.("onRemovePerson");
  }, [action]);

  const onStartVerification = useCallback(() => {
    setPersonIdvState({ status: "requires_input" });
    setQueryParam("verify-person");
    action?.("onStartVerification");
  }, [action, setQueryParam]);

  const onPersonConfigurationChangeHandler = useCallback(() => {
    action?.("onPersonConfigurationChangeHandler");
  }, [action]);

  const onNoUbosChangeHandler = useCallback(() => {
    action?.("onNoUbosChangeHandler");
  }, [action]);

  const onStartIdvFlow = useCallback(() => {
    setPersonIdvState({ status: "verified" });
    action?.("onStartIdvFlow");
  }, [action]);

  const onOpenPersonForm = useCallback(() => {
    window.scrollTo(0, 0);
    setQueryParam("add-person");
  }, [setQueryParam]);

  return children({
    documentRequirements: {
      requirements: [
        {
          name: "Certificate of Incorporation",
          href: "https://dos.ny.gov/certificate-incorporation-domestic-business-corporation",
        },
      ],
    },
    useUploadManager,
    usePersonUploadManager: useUploadManager,
    companySearchSuggestions: filteredOptions,
    fetchingCompanySearchQuery: fetching,
    personIdvState,
    backLink: "#",
    helpLink: "#",
    onSubmitClickHandler,
    onSignificantInteractionHandler,
    onConfirmClickHandler,
    isReadyForSubmit: false,
    onConfirmCancelClickHandler,
    onValidateClickHandler,
    onRemovePerson,
    onUpdatePersonClickHandler: personForm.handleSubmit(onUpdatePersonClickHandler),
    onCompanySearchTermChange,
    onStartVerification,
    onPersonConfigurationChangeHandler,
    onNoUbosChangeHandler,
    onStartIdvFlow,
    onOpenPersonForm,
    isManualCompanyEntry,
    setIsManualCompanyEntry,
    kycConfiguration,
    personForm,
    kycForm,
  });
};

const fallbackContractSignerValidation: SignerValidationByCountry = {
  validate: () => ({
    isValid: true,
  }),
};

interface MockKycWrapperProps {
  kycConfiguration: KycConfiguration;
  [otherProp: string]: any;
}

export const MockKycWrapper = (props: MockKycWrapperProps) => {
  const kycValidation = useKycValidation(props.kycConfiguration.country);
  const personValidation = usePersonValidation(props.kycConfiguration.country);
  return (
    <KycFormProvider
      defaultKycValues={{ ...defaultValues, ...props.defaultValues }}
      kycConfiguration={props.kycConfiguration}
      kycValidation={kycValidation}
      personValidation={personValidation}
      contractSignerValidation={
        signerValidationByCountry[props.kycConfiguration.country] || fallbackContractSignerValidation
      }
    >
      <FormWrapper {...(props as any)} />
    </KycFormProvider>
  );
};
